import React from 'react';
import { History, Users } from 'lucide-react';
import { darkModeClasses } from '../../utils/darkMode';
import { useSMSHistory } from '../../hooks/useSMSHistory';

interface SMSHistoryProps {
  selectedTownId?: string;
  limit?: number;
}

export const SMSHistory: React.FC<SMSHistoryProps> = ({
  selectedTownId,
  limit = 20
}) => {
  const {
    batches,
    overallStats,
    isLoading,
    hasData,
  } = useSMSHistory({ selectedTownId, limit });

  if (isLoading) {
    return (
      <div className={`${darkModeClasses.container} p-6`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm">Loading SMS history...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg md:text-xl font-bold ${darkModeClasses.textPrimary} flex items-center`}>
            <History className="h-5 w-5 md:h-6 md:w-6 mr-2 text-blue-600" />
            SMS History & Performance
          </h2>
          <div className={`text-sm ${darkModeClasses.textTertiary}`}>
            Batch-level delivery analytics
          </div>
        </div>

        {/* Overall Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`${darkModeClasses.container} p-3`}>
            <h3 className={`text-xs font-medium ${darkModeClasses.textTertiary} mb-1`}>
              Overall Success Rate
            </h3>
            <p className={`text-lg font-bold text-green-600`}>
              {overallStats.successRate.toFixed(1)}%
            </p>
          </div>
          <div className={`${darkModeClasses.container} p-3`}>
            <h3 className={`text-xs font-medium ${darkModeClasses.textTertiary} mb-1`}>
              Total Batches
            </h3>
            <p className={`text-lg font-bold ${darkModeClasses.textPrimary}`}>
              {batches.length}
            </p>
          </div>
          <div className={`${darkModeClasses.container} p-3`}>
            <h3 className={`text-xs font-medium ${darkModeClasses.textTertiary} mb-1`}>
              Total Cost
            </h3>
            <p className={`text-lg font-bold text-red-600`}>
              GHS {overallStats.totalCost.toFixed(2)}
            </p>
          </div>
          <div className={`${darkModeClasses.container} p-3`}>
            <h3 className={`text-xs font-medium ${darkModeClasses.textTertiary} mb-1`}>
              Avg Delivery Time
            </h3>
            <p className={`text-lg font-bold ${darkModeClasses.textPrimary}`}>
              {overallStats.averageDeliveryTime ? `${overallStats.averageDeliveryTime.toFixed(1)}s` : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Batches List */}
      <div className={`${darkModeClasses.container} p-6`}>
        <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>
          Message Batches
        </h3>

        {hasData ? (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {batches.map((batch, index) => (
              <div
                key={batch.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className={`font-medium ${darkModeClasses.textPrimary}`}>
                      Batch {index + 1}
                    </h4>
                    <p className={`text-sm ${darkModeClasses.textTertiary}`}>
                      {new Date(batch.timeRange.start).toLocaleString()} - {new Date(batch.timeRange.end).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      batch.summary.successRate >= 90 ? 'text-green-600' :
                      batch.summary.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {batch.summary.successRate.toFixed(1)}% Success
                    </div>
                    <div className={`text-xs ${darkModeClasses.textTertiary}`}>
                      {batch.timeRange.batchLevel} level
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className={`text-xs ${darkModeClasses.textTertiary} mb-1`}>Messages</div>
                    <div className={`flex items-center ${darkModeClasses.textPrimary}`}>
                      <Users className="h-3 w-3 mr-1" />
                      {batch.summary.totalMessages}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${darkModeClasses.textTertiary} mb-1`}>Successful</div>
                    <div className="text-green-600 font-medium">
                      {batch.summary.successfulMessages}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${darkModeClasses.textTertiary} mb-1`}>Failed</div>
                    <div className="text-red-600 font-medium">
                      {batch.summary.failedMessages}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${darkModeClasses.textTertiary} mb-1`}>Cost</div>
                    <div className="text-red-600 font-medium">
                      GHS {batch.summary.totalCost.toFixed(2)}
                    </div>
                  </div>
                </div>

                {batch.percentiles && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-xs ${darkModeClasses.textTertiary} mb-2`}>
                      Delivery Time Percentiles:
                    </div>
                    <div className="flex space-x-4 text-xs">
                      <span>P50: {batch.percentiles.deliveryTime_50th.toFixed(1)}s</span>
                      <span>P90: {batch.percentiles.deliveryTime_90th.toFixed(1)}s</span>
                      <span>P95: {batch.percentiles.deliveryTime_95th.toFixed(1)}s</span>
                      <span>P99: {batch.percentiles.deliveryTime_99th.toFixed(1)}s</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <History className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
            <p className={`${darkModeClasses.textSecondary}`}>
              No SMS batches found yet
            </p>
            <p className={`text-sm ${darkModeClasses.textTertiary} mt-1`}>
              Batched history will appear here once SMS sending begins
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
